import type { PatientEncounterSummary, PatientProfile, PmhItem } from "../../api/clinicalWorkspace";
import type { ProfileSectionKey } from "./profileSections";

/**
 * The deterministic "chart facts" summary: what a physician needs in the
 * first five seconds, built only from the record. The AI summary (next slice)
 * renders in the same card and falls back to these lines whenever the model
 * is unavailable, so nothing here may state more than the chart does.
 */

export interface ChartFactLine {
  /** Stable key for React and tests. */
  readonly key: string;
  readonly text: string;
  /** Where the line comes from; the card offers it as a jump target. */
  readonly section: ProfileSectionKey;
}

export interface ChartFacts {
  readonly headline: string;
  readonly lines: readonly ChartFactLine[];
  /** Alert chips (active allergies). Empty when none are recorded. */
  readonly alerts: readonly string[];
  /** The clinical sections were projected away for this role. */
  readonly identityOnly: boolean;
}

export function ageFromDateOfBirth(dateOfBirth: string, today = new Date()): number | undefined {
  const [year, month, day] = dateOfBirth.split("-").map((part) => Number.parseInt(part, 10));
  if (!year || !month || !day) return undefined;
  let age = today.getFullYear() - year;
  const beforeBirthday =
    today.getMonth() + 1 < month || (today.getMonth() + 1 === month && today.getDate() < day);
  if (beforeBirthday) age -= 1;
  return age >= 0 ? age : undefined;
}

export function personWord(administrativeSex: string): string {
  if (administrativeSex === "female") return "woman";
  if (administrativeSex === "male") return "man";
  return "patient";
}

function onsetYear(item: PmhItem): string | undefined {
  return item.onsetDate?.slice(0, 4);
}

function joinNames(names: readonly string[], limit: number): string {
  const shown = names.slice(0, limit);
  const rest = names.length - shown.length;
  return rest > 0 ? `${shown.join(", ")} and ${rest} more` : shown.join(", ");
}

function visitDateLabel(iso: string): string {
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime())
    ? ""
    : parsed.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

export function buildChartFacts(
  profile: PatientProfile,
  options: { readonly visits?: readonly PatientEncounterSummary[]; readonly today?: Date } = {},
): ChartFacts {
  const today = options.today ?? new Date();
  const age = ageFromDateOfBirth(profile.demographics.dateOfBirth, today);
  const person = personWord(profile.demographics.administrativeSex);
  const identityOnly = profile.pmh === undefined;

  const activeConditions = (profile.pmh ?? []).filter(
    (item) => item.status === "active" && item.category !== "surgical",
  );
  const surgical = (profile.pmh ?? []).filter(
    (item) => item.category === "surgical" && item.status !== "inactive",
  );
  const activeMedications = (profile.medications ?? []).filter((item) => item.status === "active");
  const activeAllergies = (profile.allergies ?? []).filter((item) => item.status === "active");
  const familyRows = (profile.familyHistory?.rows ?? []).filter((row) => row.status === "active");
  const narrative = profile.familyHistory?.narrative.trim() ?? "";
  const social = (profile.socialHistory ?? []).filter((item) => item.clinicalStatus === "active");
  const immunizations = (profile.immunizations ?? [])
    .filter((item) => item.status === "completed")
    .sort((a, b) => b.administeredOn.localeCompare(a.administeredOn));

  const who = age === undefined ? person : `${age}-year-old ${person}`;
  const headline = identityOnly
    ? `${who[0]?.toUpperCase() ?? ""}${who.slice(1)}`
    : activeConditions.length === 0
      ? `${who[0]?.toUpperCase() ?? ""}${who.slice(1)} with no active conditions on record`
      : `${who[0]?.toUpperCase() ?? ""}${who.slice(1)} with ${activeConditions.length} active ${
          activeConditions.length === 1 ? "condition" : "conditions"
        }`;

  const visits = options.visits ?? [];
  const lastVisit = visits[0];
  const visitLine: ChartFactLine | undefined =
    lastVisit !== undefined
      ? {
          key: "visit",
          section: "visits",
          text: `Last visit ${visitDateLabel(lastVisit.startedAt)}: ${lastVisit.reason}`,
        }
      : options.visits !== undefined
        ? { key: "visit", section: "visits", text: "No visits on record yet" }
        : undefined;

  // Priority order: what changes the visit (conditions, surgery, medications),
  // then the last visit, then the slower-moving history. The card caps at six
  // lines, so the tail drops first.
  const lines: ChartFactLine[] = [];
  if (!identityOnly) {
    if (activeConditions.length > 0) {
      lines.push({
        key: "pmh",
        section: "pmh",
        text: joinNames(
          activeConditions.map((item) => {
            const year = onsetYear(item);
            return year ? `${item.display} (since ${year})` : item.display;
          }),
          3,
        ),
      });
    }
    if (surgical.length > 0) {
      lines.push({
        key: "surgical",
        section: "pmh",
        text: `Surgical history: ${joinNames(
          surgical.map((item) => {
            const year = onsetYear(item);
            return year ? `${item.display} (${year})` : item.display;
          }),
          3,
        )}`,
      });
    }
    lines.push({
      key: "medications",
      section: "medications",
      text:
        activeMedications.length === 0
          ? "No active medications recorded"
          : `${activeMedications.length} active ${
              activeMedications.length === 1 ? "medication" : "medications"
            }: ${joinNames(
              activeMedications.map((item) => item.display),
              3,
            )}`,
    });
    if (visitLine !== undefined) lines.push(visitLine);
    if (narrative.length > 0 || familyRows.length > 0) {
      lines.push({
        key: "family",
        section: "familyHistory",
        text:
          narrative.length > 0
            ? `Family: ${narrative.length > 110 ? `${narrative.slice(0, 107).trimEnd()}…` : narrative}`
            : `Family: ${joinNames(
                familyRows.map((row) => `${row.relation.replace(/_/g, " ")} — ${row.display}`),
                3,
              )}`,
      });
    }
    if (social.length > 0) {
      lines.push({
        key: "social",
        section: "socialHistory",
        text: `Social: ${joinNames(
          social.map(
            (item) => `${item.kind} ${item.status}${item.quantity ? ` (${item.quantity})` : ""}`,
          ),
          4,
        )}`,
      });
    }
    if (immunizations.length > 0) {
      const latest = immunizations[0];
      if (latest !== undefined) {
        lines.push({
          key: "immunizations",
          section: "immunizations",
          text: `Immunizations: ${immunizations.length} on record, most recent ${latest.display} ${latest.administeredOn}`,
        });
      }
    }
  }

  if (identityOnly && visitLine !== undefined) lines.push(visitLine);

  return {
    headline,
    lines: lines.slice(0, 6),
    alerts: activeAllergies.map((item) =>
      item.reaction ? `${item.display} — ${item.reaction}` : item.display,
    ),
    identityOnly,
  };
}
